import { Component, OnInit, inject, signal, computed, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';

import { FormsModule } from '@angular/forms';
import { CdkDragDrop, moveItemInArray, DragDropModule } from '@angular/cdk/drag-drop';
import { forkJoin, firstValueFrom } from 'rxjs';

import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { MarkdownPreviewComponent } from '@rapider/markdown-preview';

import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { ProjectModelFieldControllerService } from '@rapider/sdk';

import { DataModelsDiagramComponent } from '@rapider/onboarding';
import { NotificationService } from '@rapider/services';
import { ProjectDataService, ProjectDataActions, selectProjectModelsList } from '@rapider/project-data';
import { Store } from '@ngrx/store';

// Used to delay the fetching of the updated schema after field changes to simulate backend processing time and show the loading state in the UI
export const PROJECT_MODEL_REFRESH_DELAY = 5000; // 5 seconds, simulating backend processing time for schema changes

import {
  DATABASE_CONFIG,
  extractAiConfigFromMetadata,
  extractTitleTemplateFromMetadata,
  hasDetailedAiConfig,
  buildSingleModelGraphData
} from '../database.config';
import { ModelEndpointsTableComponent } from '../model-endpoints-table/model-endpoints-table.component';

import { ProjectModelDataViewerComponent } from '../project-model-data-viewer/project-model-data-viewer.component';
import { ProjectModelSpreadsheetViewerComponent } from '../project-model-spreadsheet-viewer/project-model-spreadsheet-viewer.component';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { isObject } from 'lodash';

@Component({
  selector: 'rapider-project-model-detail',
  standalone: true,
  imports: [
    CommonModule,
    NzTableModule,
    NzTagModule,
    NzTooltipModule,
    NzButtonModule,
    NzModalModule,
    NzInputModule,
    NzSelectModule,
    NzSwitchModule,
    NzPopconfirmModule,
    FormsModule,
    DragDropModule,
    MarkdownPreviewComponent,
    DataModelsDiagramComponent,
    ModelEndpointsTableComponent,
    ProjectModelDataViewerComponent,
    ProjectModelSpreadsheetViewerComponent
  ],
  templateUrl: './project-model-detail.component.html',
  host: {
    class: 'block w-full min-h-full bg-body-background'
  }
})
export class ProjectModelDetailComponent implements OnInit {

  /**
   * Toggle to switch between Data, Spreadsheet, and Schema views.
   * Defaults to 'data' per requirements.
   */
  public activeView = signal<'data' | 'spreadsheet' | 'schema'>('data');

  public isTitleHovered = signal<boolean>(false);

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  private readonly projectDataService = inject(ProjectDataService);
  private readonly notificationService = inject(NotificationService);
  private readonly store = inject(Store);

  public readonly config = DATABASE_CONFIG;

  // Single entity state
  private readonly projectModelFieldController = inject(ProjectModelFieldControllerService);

  // Field Management State
  public isFieldModalVisible = signal<boolean>(false);
  public isFieldSaving = signal<boolean>(false);
  public editingFieldId = signal<string | null>(null);
  public isExplanationCollapsed = signal<boolean>(true);
  public nameError = signal<string | null>(null);

  public fieldForm = signal<any>({
    name: '',
    title: '',
    type: 'string',
    description: '',
    isRequired: false,
    isUnique: false,
    isPrimaryKey: false,
    isHidden: false,
    isSensitive: false,
    isArray: false,
    default: '',
    defaultFn: null,
    hasEnum: false,
    enumValues: [],
    isCalculated: false,
    calculationFormula: ''
  });

  public fieldTypes = ['string', 'number', 'boolean', 'date', 'object', 'any'];

  /** Expose pure function to template */
  public isSystemField = isSystemField;

  /** Tracks whether the schema was modified to conditionally show the migration warning */
  public hasSchemaChanges = signal<boolean>(false);

  // Enum Form UI State
  public enumInputValue = signal<string>('');
  public editingEnumIndex = signal<number | null>(null);
  public editingEnumValue = signal<string>('');

  public model = signal<any>(null);

  // UI tracking flags
  public isLoading = signal<boolean>(true);

  /** Background refresh state for non-blocking schema reloads */
  public isSchemaRefreshing = signal<boolean>(false);
  private schemaRefreshTimeout: any;

  // CRITICAL FIX: Track fullscreen state for the data section
  public isDataFullscreen = signal<boolean>(false);

  // CRITICAL FIX: Increment this to force child data components to re-fetch on demand
  public dataRefreshTrigger = signal<number>(0);

  // Pure computed resolutions mapping to the metadata array structures
  public extractedAiConfig = computed(() => extractAiConfigFromMetadata(this.model()));
  public extractedTitleTemplate = computed(() => extractTitleTemplateFromMetadata(this.model()));

  // Wrapper check to control section visibility natively in HTML block
  public hasValidAiConfig = computed(() => hasDetailedAiConfig(this.extractedAiConfig()));

  // Pre-computes the individual local graph map for the active model
  public graphData = computed(() => buildSingleModelGraphData(this.model()));

ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    const viewParam = this.route.snapshot.queryParamMap.get('view');
    
    if (viewParam === 'schema' || viewParam === 'spreadsheet' || viewParam === 'data') {
      this.activeView.set(viewParam as any);
    }

    if (id) {
      this.fetchModelDetail(id, true);
    } else {
      this.goBack();
    }
  }

  /**
   * Fetches complete metadata for a given project model entity.
   * Uses the ProjectDataService wrapper to leverage NgRx caching efficiency unless forceRefresh is true.
   * @param {string} id - Active entity identifier mapping to backend document.
   * @param {boolean} forceRefresh - Bypasses cache and explicitly calls the API, then updates NgRx.
   * @param {boolean} isBackground - If true, avoids triggering the full-page isLoading spinner.
   */
  private fetchModelDetail(id: string, forceRefresh = false, isBackground = false): void {
    if (!isBackground) {
      this.isLoading.set(true);
    }

    const fetchObservable = forceRefresh
      ? this.projectDataService.fetchProjectModelCompleteDetail(id) // Bypasses cache to hit API directly
      : this.projectDataService.getProjectModelCompleteDetail(id);  // Uses NgRx cache

    fetchObservable.subscribe({
      next: (mergedModel) => {
        this.model.set(mergedModel);

        // If we forced a refresh, update the global NgRx cache manually to persist the new fields
        if (forceRefresh) {
          const cachedModels = this.store.selectSignal(selectProjectModelsList)();
          if (cachedModels) {
            const updatedModelsList = [mergedModel, ...cachedModels.filter((m: any) => m.id !== id)];
            this.store.dispatch(ProjectDataActions.setProjectModels({ models: updatedModelsList }));
          }
        }

        this.isLoading.set(false);
        this.isSchemaRefreshing.set(false);
      },
      error: () => {
        this.notificationService.createNotification('error', 'Error', this.config.notifications.fetchDetailFailed);
        this.isLoading.set(false);
        this.isSchemaRefreshing.set(false);
        this.goBack();
      }
    });
  }

  /**
   * Pure routing fallback handler backing out into the overall list schema.
   */
  public goBack(): void {
    this.router.navigate(['/database']);
  }

  /**
   * Toggles the local fullscreen state for the Data Viewer section.
   * If transitioning into fullscreen, it locks the body scroll to prevent double scrolling.
   */
  public toggleDataFullscreen(): void {
    this.isDataFullscreen.update(state => {
      const newState = !state;
      if (typeof document !== 'undefined') {
        if (newState) {
          document.body.style.overflow = 'hidden';
        } else {
          document.body.style.overflow = '';
        }
      }
      return newState;
    });
  }

  /**
   * Forces a refresh of the child data viewers by incrementing the trigger signal,
   * which is passed down via an @Input to trigger ngOnChanges.
   */
  public refreshData(): void {
    this.dataRefreshTrigger.update(val => val + 1);
  }

  /**
     * Opens the modal to add a new field or edit an existing one.
     * Excludes internal properties like id, isMixinProperty, jsonSchema, isId from the form.
     * @param {any} field - Optional field data to populate the edit form.
     */
  public openFieldModal(field?: any): void {
    this.nameError.set(null);
    this.isExplanationCollapsed.set(true);

    if (field) {
      const enums = field.jsonSchema?.enum || [];
      this.editingFieldId.set(field.id);
      this.fieldForm.set({
        name: field.name || '',
        title: field.title || '',
        type: field.type || 'string',
        description: field.description || '',
        isRequired: field.isRequired || false,
        isUnique: field.isUnique || false,
        isPrimaryKey: field.isPrimaryKey || false,
        isHidden: field.isHidden || false,
        isSensitive: field.isSensitive || false,
        isArray: field.isArray || false,
        default: field.default || '',
        defaultFn: field.defaultFn || null,
        hasEnum: enums.length > 0,
        enumValues: enums,
        isCalculated: !!field.calculation,
        calculationFormula: field.calculation?.formula || ''
      });
    } else {
      this.editingFieldId.set(null);
      this.fieldForm.set({
        name: '',
        title: '',
        type: 'string',
        description: '',
        isRequired: false,
        isUnique: false,
        isPrimaryKey: false,
        isHidden: false,
        isSensitive: false,
        isArray: false,
        default: '',
        defaultFn: null,
        hasEnum: false,
        enumValues: [],
        isCalculated: false,
        calculationFormula: ''
      });
    }
    this.isFieldModalVisible.set(true);
  }

  /**
   * Closes the field management modal.
   */
  public closeFieldModal(): void {
    this.isFieldModalVisible.set(false);
  }

  /**
   * Auto-generates the field title based on the field name upon blur if empty.
   */
  public autoGenerateTitle(): void {
    const form = this.fieldForm();
    if (!form.title && form.name) {
      this.fieldForm.update(f => ({ ...f, title: generateTitleFromName(f.name) }));
    }
  }


  /**
   * Auto-fixes the field name to camelCase if it is currently invalid.
   */
  public fixFieldName(): void {
    const currentName = this.fieldForm().name;
    if (currentName) {
      const fixedName = sanitizeToCamelCase(currentName);
      this.fieldForm.update(f => ({ ...f, name: fixedName }));

      // Re-validate to clear the error if it's now valid
      const m = this.model();
      const errorKey = validateFieldName(fixedName, m.fields || [], this.editingFieldId());
      if (errorKey) {
        this.nameError.set((this.config.texts as any)[errorKey]);
      } else {
        this.nameError.set(null);
      }
    }
  }

  /**
   * Adds a new enum value to the field form array, preventing duplicates and empty values.
   */
  public addEnumValue(): void {
    const val = this.enumInputValue().trim();
    if (!val) {
      this.notificationService.createNotification('warning', 'Validation', this.config.texts.enumEmptyError);
      return;
    }
    const currentEnums = this.fieldForm().enumValues || [];
    if (currentEnums.includes(val)) {
      this.notificationService.createNotification('warning', 'Validation', this.config.texts.enumDuplicateError);
      return;
    }
    this.fieldForm.update(f => ({ ...f, enumValues: [...currentEnums, val] }));
    this.enumInputValue.set('');
  }

  /**
   * Prepares an existing enum value for inline editing.
   * @param {number} index - Index of the enum value.
   * @param {string} value - Current value.
   */
  public startEditEnum(index: number, value: string): void {
    this.editingEnumIndex.set(index);
    this.editingEnumValue.set(value);
  }

  /**
   * Saves the modified enum value.
   */
  public saveEditEnum(): void {
    const index = this.editingEnumIndex();
    const val = this.editingEnumValue().trim();
    if (index === null) return;

    if (!val) {
      this.notificationService.createNotification('warning', 'Validation', this.config.texts.enumEmptyError);
      return;
    }

    const currentEnums = [...(this.fieldForm().enumValues || [])];

    // Check for duplicates excluding the current item being edited
    if (currentEnums.some((e, i) => e === val && i !== index)) {
      this.notificationService.createNotification('warning', 'Validation', this.config.texts.enumDuplicateError);
      return;
    }

    currentEnums[index] = val;
    this.fieldForm.update(f => ({ ...f, enumValues: currentEnums }));
    this.editingEnumIndex.set(null);
    this.editingEnumValue.set('');
  }

  /**
   * Cancels the enum editing mode.
   */
  public cancelEditEnum(): void {
    this.editingEnumIndex.set(null);
    this.editingEnumValue.set('');
  }

  /**
   * Removes an enum value by its index.
   * @param {number} index - The index of the item to remove.
   */
  public removeEnumValue(index: number): void {
    const currentEnums = [...(this.fieldForm().enumValues || [])];
    currentEnums.splice(index, 1);
    this.fieldForm.update(f => ({ ...f, enumValues: currentEnums }));
  }

  /**
   * Handles reordering of the enum values via drag and drop.
   * @param {CdkDragDrop<string[]>} event - Drag drop event from CDK.
   */
  public onEnumDrop(event: CdkDragDrop<string[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    const updatedEnums = [...this.fieldForm().enumValues];
    moveItemInArray(updatedEnums, event.previousIndex, event.currentIndex);
    this.fieldForm.update(f => ({ ...f, enumValues: updatedEnums }));
  }

  /**
   * Validates and saves the field configuration.
   * Translates the UI `isCalculated` toggle into the strict `calculation` object format,
   * and maps the `enumValues` into `jsonSchema.enum`.
   */
  public async saveField(): Promise<void> {
    const form = this.fieldForm();
    const m = this.model();

    // Pure validation
    const errorKey = validateFieldName(form.name, m.fields || [], this.editingFieldId());
    if (errorKey) {
      this.nameError.set((this.config.texts as any)[errorKey]);
      return;
    }

    if (!form.type) {
      this.notificationService.createNotification('warning', 'Validation', 'Type is required.');
      return;
    }

    // Auto-generate title implicitly on save if user didn't blur the field
    if (!form.title) {
      form.title = generateTitleFromName(form.name);
    }

    this.isFieldSaving.set(true);

    const payload: any = {
      name: form.name,
      title: form.title,
      type: form.type,
      description: form.description,
      isRequired: form.isRequired,
      isUnique: form.isUnique,
      isPrimaryKey: form.isPrimaryKey,
      isHidden: form.isHidden,
      isSensitive: form.isSensitive,
      isArray: form.isArray,
      projectId: m.projectId,
      projectModelId: m.id
    };

    // Only inject default fields if they actually contain a value
    if (form.default && form.default.toString().trim() !== '') {
      payload.default = form.default;
    }
    if (form.defaultFn && form.defaultFn.trim() !== '') {
      payload.defaultFn = form.defaultFn;
    }

    if (form.hasEnum && form.enumValues && form.enumValues.length > 0) {
      // Remove any accidental duplicates automatically
      const uniqueEnums = Array.from(new Set(form.enumValues));
      payload.jsonSchema = { type: form.type, enum: uniqueEnums };
    }

    if (form.isCalculated && form.calculationFormula && form.calculationFormula.trim() !== '') {
      payload.calculation = { type: 'jsonata', formula: form.calculationFormula.trim() };
    }

    const id = this.editingFieldId();
    try {

      // Explicitly NULL out removed complex objects so Loopback deletes them from DB
      if (!payload.calculation) {
        delete payload.calculation; // Don't send the property at all if it's not calculated to prevent accidental deletion of existing calculation on save
      }

      if (!payload.jsonSchema || (isObject(payload.jsonSchema) && Object.keys(payload.jsonSchema).length === 0)) {
        delete payload.jsonSchema;
      }
      if (!payload.defaultFn) {
        delete payload.defaultFn;
      }
      if (!payload.default) {
        delete payload.default;
      }

      if (id) {

        await firstValueFrom(this.projectModelFieldController.updateById({ id, body: payload }));
        this.notificationService.createNotification('success', 'Success', 'Field updated successfully. Applying changes and refreshing schema...');
      } else {
        // New field, set index to the end
        payload.index = m.fields ? m.fields.length : 0;
        await firstValueFrom(this.projectModelFieldController.create({ body: payload }));
        this.notificationService.createNotification('success', 'Success', 'Field created successfully. Applying changes and refreshing schema...');
      }

      this.hasSchemaChanges.set(true);
      this.closeFieldModal();

      // Non-blocking refresh: Show table loading and simulate 10-second backend delay
      this.isSchemaRefreshing.set(true);
      if (this.schemaRefreshTimeout) clearTimeout(this.schemaRefreshTimeout);
      this.schemaRefreshTimeout = setTimeout(() => {
        this.fetchModelDetail(m.id, true, true); // forceRefresh=true, isBackground=true
      }, PROJECT_MODEL_REFRESH_DELAY);

    } catch (error: any) {
      console.error('Failed to save field', error);
      this.notificationService.createNotification('error', 'Error', error?.message || 'Failed to save field.');
    } finally {
      this.isFieldSaving.set(false);
    }
  }

  /**
     * Deletes a field natively from the database after confirmation.
     * @param {string} fieldId - The unique identifier of the field.
     */
  public async deleteField(fieldId: string): Promise<void> {
    try {
      await firstValueFrom(this.projectModelFieldController.deleteById({ id: fieldId }));
      this.notificationService.createNotification('success', 'Success', 'Field deleted successfully. Applying changes and refreshing schema...');
      this.hasSchemaChanges.set(true);

      // Non-blocking refresh: Show table loading and simulate 10-second backend delay
      this.isSchemaRefreshing.set(true);
      if (this.schemaRefreshTimeout) clearTimeout(this.schemaRefreshTimeout);
      this.schemaRefreshTimeout = setTimeout(() => {
        this.fetchModelDetail(this.model().id, true, true); // forceRefresh=true, isBackground=true
      }, PROJECT_MODEL_REFRESH_DELAY);

    } catch (error: any) {
      console.error('Failed to delete field', error);
      this.notificationService.createNotification('error', 'Error', error?.message || 'Failed to delete field.');
    }
  }

  /**
   * Handles the drag-and-drop reordering of fields natively.
   * Recalculates the index of the affected fields and performs parallel updates.
   * @param {CdkDragDrop<any[]>} event - The drag and drop event payload from CDK.
   */
  public onFieldDrop(event: CdkDragDrop<any[]>): void {
    if (event.previousIndex === event.currentIndex) return;

    const currentModel = this.model();
    if (!currentModel || !currentModel.fields) return;

    // Boundary checks: Prevent dropping outside editable boundaries (id / mixin properties)
    const firstEditableIndex = currentModel.fields.findIndex((f: any) => !isSystemField(f));
    const lastEditableIndex = currentModel.fields.findLastIndex((f: any) => !isSystemField(f));

    if (event.currentIndex < firstEditableIndex || event.currentIndex > lastEditableIndex) {
      this.notificationService.createNotification('warning', 'Invalid Action', this.config.texts.invalidDropPosition);
      return;
    }

    const updatedFields = [...currentModel.fields];
    moveItemInArray(updatedFields, event.previousIndex, event.currentIndex);

    // CRITICAL FIX: Deep clone objects before mutating index property since NgRx store arrays contain read-only objects
    const clonedFields = updatedFields.map((f, i) => ({ ...f, index: i }));

    // Optimistically update the UI to prevent jank
    this.model.set({ ...currentModel, fields: clonedFields });

    // Calculate the slice of fields that were actually affected by the shift
    const minIdx = Math.min(event.previousIndex, event.currentIndex);
    const maxIdx = Math.max(event.previousIndex, event.currentIndex);
    const affectedFields = clonedFields.slice(minIdx, maxIdx + 1);

    // Execute bulk update pattern using forkJoin for absolute reliability
    const updateRequests = affectedFields.map(f =>
      this.projectModelFieldController.updateById({ id: f.id, body: { index: f.index } as any })
    );

    forkJoin(updateRequests).subscribe({
      next: () => {
        this.notificationService.createNotification('success', 'Success', 'Field order updated.');
        this.hasSchemaChanges.set(true);
      },
      error: (err) => {
        console.error('Failed to update field order', err);
        this.notificationService.createNotification('error', 'Error', 'Failed to update field order.');
        this.fetchModelDetail(currentModel.id); // Revert to server state on failure
      }
    });
  }
}

/**
 * PURE FUNCTION: Evaluates whether a field is restricted from deletion/editing/ordering.
 * @param {any} field - The field configuration object.
 * @returns {boolean} True if the field is a system field.
 */
export function isSystemField(field: any): boolean {
  if (!field) return false;
  return field.name === 'id' || field.isId === true || field.isMixinProperty === true;
}

/**
 * PURE FUNCTION: Auto-generates a user-friendly title based on the data field name.
 * Handles both camelCase and snake_case formatting.
 * @param {string} name - The raw field name.
 * @returns {string} The formatted Title Case string.
 */
export function generateTitleFromName(name: string): string {
  if (!name) return '';
  return name
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, c => c.toUpperCase())
    .trim();
}

/**
 * PURE FUNCTION: Sanitizes a string into strict camelCase format.
 * Accurately capitalizes words after spaces or special characters.
 * @param {string} str - The raw string to sanitize.
 * @returns {string} The camelCase formatted string.
 */
export function sanitizeToCamelCase(str: string): string {
  if (!str) return '';

  // Replace anything that is not a letter or number with a space
  const cleanStr = str.replace(/[^a-zA-Z0-9]+/g, ' ').trim();

  // Split by spaces and capitalize appropriately
  const words = cleanStr.split(/\s+/);

  return words.map((word, index) => {
    if (index === 0) {
      // First word is entirely lowercase
      return word.charAt(0).toLowerCase() + word.slice(1).toLowerCase();
    }
    // Subsequent words are capitalized
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  }).join('');
}

/**
 * PURE FUNCTION: Validates field name strings against rules and checks for duplicates.
 * @param {string} name - The inputted field name.
 * @param {any[]} fields - Existing fields in the model.
 * @param {string | null} editingId - ID of the field if in edit mode (to skip self-duplicate check).
 * @returns {string | null} Error string translation key, or null if valid.
 */
export function validateFieldName(name: string, fields: any[], editingId: string | null): string | null {
  if (!name || name.trim() === '') return 'invalidFieldName';

  // Enforces camelCase or snake_case: must start with a lowercase letter, followed by letters, numbers, or underscores
  const regex = /^[a-z][a-zA-Z0-9_]*$/;
  if (!regex.test(name)) return 'invalidFieldName';

  const duplicate = fields.find(f => f.name.toLowerCase() === name.toLowerCase() && f.id !== editingId);
  if (duplicate) return 'duplicateFieldName';

  return null;
}